import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { addDays, isAfter, isBefore } from 'date-fns';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { ConfigService } from '@mona/config';
import { DailyGoal, DataUpdateMessage, DataUpdateMessageOperation } from '@mona/models';
import { ChangeLogService } from '@mona/pdms/data-access-changelog';
import { applyLastChangedByToPrescriptions } from '@mona/pdms/data-access-combined';
import { PractitionersFacade } from '@mona/pdms/data-access-practitioners';
import { DailyGoalsState } from '../entities';
import { DailyGoalsSelectors } from '../state';
import { DailyGoalsActions } from '../state/actions/daily-goals.actions';
import { selectDailyGoalAll, selectDailyGoalsEntities } from '../state/selectors/daily-goals.selectors';

/**
 * Notifications store service
 */
@Injectable({ providedIn: 'root' })
export class DataAccessDailyGoalsFacade {
    /** Get daily goals map */
    readonly dailyGoals$: Observable<{ active: DailyGoal[]; inactive: DailyGoal[] }> = this.store
        .select(selectDailyGoalAll)
        .pipe(
            map(dailyGoals => {
                const todayResetTime = this.getGoalsResetDate();
                const activeBoundaries = isAfter(new Date(), todayResetTime)
                    ? {
                          start: todayResetTime,
                          end: addDays(todayResetTime, 1),
                      }
                    : {
                          start: addDays(todayResetTime, -1),
                          end: todayResetTime,
                      };

                return dailyGoals.reduce(
                    (acc, dailyGoal) => {
                        const isActiveGoal =
                            isAfter(dailyGoal.date, activeBoundaries.start) &&
                            isBefore(dailyGoal.date, activeBoundaries.end);

                        acc[isActiveGoal ? 'active' : 'inactive'].push(dailyGoal);
                        return acc;
                    },
                    { active: [], inactive: [] },
                );
            }),
        );

    /** daily goals map */
    readonly dailyGoalsMap$: Observable<Record<string, DailyGoal>> = this.store.select(selectDailyGoalsEntities);

    /** is daily goals data loading */
    readonly isLoading$ = this.store.select(DailyGoalsSelectors.selectIsLoading);

    private _resetDate: Date;
    private _resetDateDay: number; // Track which day the cached reset date is for

    /**
     * Constructor
     * @param store store
     * @param changeLogService
     * @param configService
     * @param practitionersFacade
     */
    constructor(
        private store: Store<DailyGoalsState>,
        private changeLogService: ChangeLogService,
        private configService: ConfigService,
        private practitionersFacade: PractitionersFacade,
    ) {}

    /** load daily goals data */
    async loadDailyGoalsData(debounceTime = 0): Promise<void> {
        // NOTE: API just don't have the freshest data without any delays
        await new Promise(resolve => setTimeout(resolve, debounceTime));
        this.store.dispatch(DailyGoalsActions.loadDailyGoals());
    }

    /**
     * Reset instant for the current local day from balanceCalculationResetTime (wall clock, not UTC).
     * Same Date drives UI subtitle, active/inactive split, and clear scheduler.
     */
    getGoalsResetDate(): Date {
        const now = new Date();
        const todayDay = now.getDate();

        // Recalculate if no cached date or if cached date is for a different local day
        if (!this._resetDate || this._resetDateDay !== todayDay) {
            const balanceResetTime = this.configService.get('balanceCalculationResetTime');
            const wallHours = balanceResetTime ? +balanceResetTime.split(':')[0] : 5; // 5 a.m local fallback
            this._resetDate = new Date(new Date().setHours(wallHours, 0, 0, 0));
            this._resetDateDay = todayDay;
        }
        return this._resetDate;
    }

    /**
     * INFO: add comment
     * @param goal
     */
    createDailyGoal(goal: Partial<DailyGoal>): void {
        this.changeLogService.createDailyGoal(goal);
    }

    /**
     * INFO: add comment
     * @param goal
     */
    updateDailyGoal(goal: Partial<DailyGoal>): void {
        this.changeLogService.updateDailyGoal(goal);
    }

    /**
     * INFO: add comment
     * @param id
     */
    removeDailyGoal(id: string): void {
        this.changeLogService.removeDailyGoal(id);
    }

    /**
     * Clear daily goals store
     */
    clearDailyGoals(): void {
        this.store.dispatch(DailyGoalsActions.clearDailyGoals());
    }

    /**
     * Handle DailyGoal live data update
     * @param updateMessage
     */
    handleDailyGoalDataUpdateMessage(updateMessage: DataUpdateMessage<DailyGoal>): void {
        if (updateMessage.operation === DataUpdateMessageOperation.Delete) {
            this.store.dispatch(DailyGoalsActions.removeDailyGoals({ ids: [updateMessage.modelId] }));
            return;
        }

        this.practitionersFacade.practitionersMap$.pipe(take(1)).subscribe(practitionersMap => {
            const data = applyLastChangedByToPrescriptions(
                [{ ...updateMessage.payload, id: updateMessage.modelId } as DailyGoal],
                practitionersMap,
            ) as DailyGoal[];
            this.store.dispatch(DailyGoalsActions.upsertDailyGoals({ data }));
        });
    }
}
